import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchApiKeys, createApiKey, revokeApiKey, rotateApiKey } from '../../actions/apiKeyActions';

const SCOPES = ['read', 'write', 'admin'];

export default function ApiKeys() {
  const dispatch = useDispatch();
  const { items, loading } = useSelector(s => s.apiKeys);
  const [draft, setDraft] = useState({ name: '', scopes: ['read'] });
  const [created, setCreated] = useState(null);   // the plaintext token, shown once
  const [status, setStatus] = useState({ kind: '', text: '' });

  useEffect(() => { dispatch(fetchApiKeys()); }, [dispatch]);

  const toggleScope = (s) => setDraft(d => ({ ...d, scopes: d.scopes.includes(s) ? d.scopes.filter(x => x !== s) : [...d.scopes, s] }));

  const create = async (e) => {
    e.preventDefault();
    if (!draft.name) return;
    const res = await dispatch(createApiKey(draft.name, draft.scopes));
    if (res.ok) {
      setCreated(res.token);
      setDraft({ name: '', scopes: ['read'] });
      setStatus({ kind: 'success', text: 'Token created. Copy it now — you will not see it again.' });
    } else setStatus({ kind: 'error', text: res.error });
  };
  const rotate = async (id) => {
    if (!confirm('Rotate this key? The old secret stops working immediately.')) return;
    const res = await dispatch(rotateApiKey(id));
    if (res.ok) { setCreated(res.token); setStatus({ kind: 'success', text: 'Token rotated. Save the new secret.' }); dispatch(fetchApiKeys()); }
    else setStatus({ kind: 'error', text: res.error });
  };

  return (
    <section className="section">
      <h1>API keys</h1>
      <p className="muted">Personal access tokens for the Auto Showroom JSON API. Use them in <code>Authorization: Bearer ats_...</code> headers from scripts, CI jobs, or external integrations.</p>

      <div className="card">
        <h2>Create a new key</h2>
        <form className="auth-form" onSubmit={create} style={{maxWidth:'520px'}}>
          <label>Label<input required value={draft.name} onChange={e => setDraft({...draft, name: e.target.value})} placeholder="e.g. CI server, Zapier" /></label>
          <label>Scopes</label>
          <div style={{display:'flex',gap:'1rem',marginBottom:'1rem'}}>
            {SCOPES.map(s => (
              <label key={s} style={{display:'flex',alignItems:'center',gap:'.4rem',fontSize:'.95rem',fontWeight:400}}>
                <input type="checkbox" checked={draft.scopes.includes(s)} onChange={() => toggleScope(s)} /> {s}
              </label>
            ))}
          </div>
          <button className="btn-primary" type="submit">Create token</button>
        </form>
      </div>

      {created && (
        <div className="card" style={{borderColor:'#7ee787',background:'#f4fdf6'}}>
          <h2>Save this token — you will not see it again</h2>
          <p style={{fontFamily:'ui-monospace,Menlo,monospace',fontSize:'1rem',background:'#0a1a2e',color:'#7ee787',padding:'1rem',borderRadius:'6px',wordBreak:'break-all'}}>{created.token}</p>
          <button className="btn-ghost" type="button" onClick={() => navigator.clipboard.writeText(created.token)}>Copy to clipboard</button>
          <button className="btn-ghost" type="button" onClick={() => setCreated(null)} style={{marginLeft:'.6rem'}}>I've saved it</button>
        </div>
      )}

      <div className="card">
        <h2>Existing keys ({items.length})</h2>
        <table className="data-table">
          <thead><tr><th>Label</th><th>Prefix</th><th>Scopes</th><th>Last used</th><th>Created</th><th style={{textAlign:'right'}}>Actions</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="muted">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="6" className="muted">No keys yet.</td></tr>}
            {items.map(k => (
              <tr key={k._id}>
                <td><strong>{k.name}</strong></td>
                <td><code>{k.prefix}…</code></td>
                <td>{k.scopes.join(', ')}</td>
                <td>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}</td>
                <td>{new Date(k.createdAt).toLocaleDateString()}</td>
                <td style={{textAlign:'right'}}>
                  <button className="btn-ghost" style={{marginRight:'.4rem',padding:'.3rem .7rem'}} onClick={() => rotate(k._id)}>Rotate</button>
                  <button onClick={() => confirm('Revoke this key permanently?') && dispatch(revokeApiKey(k._id))} style={{padding:'.3rem .7rem',border:'1.5px solid #f0c4c4',background:'white',color:'#b53b3b',borderRadius:'6px',cursor:'pointer',fontWeight:600,fontSize:'.85rem'}}>Revoke</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {status.text && <p className={`status ${status.kind}`}>{status.text}</p>}
    </section>
  );
}
